import React, { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/common/ProtectedRoute';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { 
  getDistribution, saveCmsDocument, deleteCmsDocument, reorderCmsDocuments,
  getSiteContent, saveSiteContent 
} from '../../lib/cmsService';
import { DistributionTitle, ContentStatus, SiteContentConfig } from '../../types';
import { MediaPickerModal } from '../../components/admin/MediaPickerModal';
import { StargazeImage } from '../../components/common/StargazeImage';
import { 
  Globe, Plus, Edit3, Trash2, ArrowUp, ArrowDown, Check, X, 
  RefreshCw, Image as ImageIcon, AlertCircle, Film, Tv, Video,
  CheckCircle2, Sliders, Sparkles, Search, Filter, Layers, ExternalLink
} from 'lucide-react';

const COMMON_TERRITORIES = [
  'Worldwide', 'North America', 'Europe', 'United Kingdom', 
  'Asia-Pacific', 'Latin America', 'Middle East & North Africa', 
  'India / South Asia', 'Australia & New Zealand', 'Global Non-Theatrical'
];

const COMMON_RIGHTS = [
  'Theatrical', 'VOD / SVOD', 'Linear Television & Broadcast', 
  'Inflight / Airline Rights', 'Physical Home Video (4K/Blu-Ray)', 
  'Format / Remake Rights', 'Festival & Cultural Screenings', 'Pay-Per-View'
];

export const DistributionAdminPage: React.FC = () => {
  const [distribution, setDistribution] = useState<DistributionTitle[]>([]);
  const [siteContent, setSiteContent] = useState<SiteContentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingHeader, setSavingHeader] = useState(false);
  const [headerSaved, setHeaderSaved] = useState(false);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Edit Modal State
  const [editingItem, setEditingItem] = useState<Partial<DistributionTitle> | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DistributionTitle | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [customTerritoryInput, setCustomTerritoryInput] = useState('');
  const [customRightInput, setCustomRightInput] = useState('');

  // Section Header Customization Drawer/Panel
  const [showHeaderCustomizer, setShowHeaderCustomizer] = useState(false);
  const [sectionEyebrow, setSectionEyebrow] = useState('08 // GLOBAL LICENSING');
  const [sectionHeading, setSectionHeading] = useState('DISTRIBUTION CATALOGUE');
  const [sectionDescription, setSectionDescription] = useState('Worldwide theatrical, streaming, and linear distribution rights.');
  const [sectionCtaLabel, setSectionCtaLabel] = useState('VIEW FULL CATALOGUE');
  const [sectionCtaDestination, setSectionCtaDestination] = useState('/distribution');

  const loadData = async () => {
    setLoading(true);
    try {
      const [distData, contentData] = await Promise.all([
        getDistribution(),
        getSiteContent(),
      ]);
      setDistribution(distData);
      setSiteContent(contentData);

      // Populate section config if present
      const distSection = contentData?.homepageSections?.distribution;
      if (distSection) {
        setSectionEyebrow(distSection.eyebrow || '08 // GLOBAL LICENSING');
        setSectionHeading(distSection.heading || 'DISTRIBUTION CATALOGUE');
        setSectionDescription(distSection.description || 'Worldwide theatrical, streaming, and linear distribution rights.');
        setSectionCtaLabel(distSection.ctaLabel || 'VIEW FULL CATALOGUE');
        setSectionCtaDestination(distSection.ctaDestination || '/distribution');
      }
    } catch (err) {
      console.error('Error loading distribution:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !editingItem.title) return;
    setSaving(true);

    try {
      const id = editingItem.id || `dist-${Date.now()}`;
      const payload: DistributionTitle = {
        id,
        title: editingItem.title || '',
        type: editingItem.type || 'Film',
        synopsis: editingItem.synopsis || '',
        territories: editingItem.territories && editingItem.territories.length > 0 ? editingItem.territories : ['Worldwide'],
        rightsAvailable: editingItem.rightsAvailable && editingItem.rightsAvailable.length > 0 ? editingItem.rightsAvailable : ['Theatrical', 'VOD / SVOD'],
        posterUrl: editingItem.posterUrl || 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?auto=format&fit=crop&w=800&q=80',
        coverMediaId: editingItem.coverMediaId || '',
        status: editingItem.status || 'PUBLISHED',
        featured: editingItem.featured ?? true,
        displayOrder: editingItem.displayOrder ?? (distribution.length + 1),
        focalPoint: editingItem.focalPoint || { x: 50, y: 50 },
        updatedAt: new Date().toISOString(),
      };

      await saveCmsDocument('distribution', id, payload, 'SAVE_DISTRIBUTION_TITLE');
      await loadData();
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving distribution title:', err);
      alert('Failed to save distribution title');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: DistributionTitle) => {
    try {
      await deleteCmsDocument('distribution', item.id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting title:', err);
      alert('Failed to delete distribution title');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= distribution.length) return;

    const reordered = [...distribution];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);

    setDistribution(reordered);
    await reorderCmsDocuments('distribution', reordered.map((d) => d.id));
  };

  const handleSaveSectionHeader = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingHeader(true);
    setHeaderSaved(false);

    try {
      const currentContent = siteContent || (await getSiteContent());
      const updatedContent: SiteContentConfig = {
        ...currentContent,
        homepageSections: {
          ...(currentContent?.homepageSections || {}),
          distribution: {
            id: 'distribution',
            enabled: true,
            eyebrow: sectionEyebrow,
            heading: sectionHeading,
            description: sectionDescription,
            ctaLabel: sectionCtaLabel,
            ctaDestination: sectionCtaDestination,
            displayOrder: 8,
          },
        },
      };

      await saveSiteContent(updatedContent);
      setSiteContent(updatedContent);
      setHeaderSaved(true);
      setTimeout(() => setHeaderSaved(false), 3000);
    } catch (err) {
      console.error('Error saving section header config:', err);
      alert('Failed to save section header settings');
    } finally {
      setSavingHeader(false);
    }
  };

  const toggleTerritory = (territory: string) => {
    if (!editingItem) return;
    const current = editingItem.territories || [];
    const exists = current.includes(territory);
    const updated = exists ? current.filter((t) => t !== territory) : [...current, territory];
    setEditingItem({ ...editingItem, territories: updated });
  };

  const addCustomTerritory = () => {
    if (!customTerritoryInput.trim() || !editingItem) return;
    const current = editingItem.territories || [];
    if (!current.includes(customTerritoryInput.trim())) {
      setEditingItem({ ...editingItem, territories: [...current, customTerritoryInput.trim()] });
    }
    setCustomTerritoryInput('');
  };

  const toggleRight = (right: string) => {
    if (!editingItem) return;
    const current = editingItem.rightsAvailable || [];
    const exists = current.includes(right);
    const updated = exists ? current.filter((r) => r !== right) : [...current, right];
    setEditingItem({ ...editingItem, rightsAvailable: updated });
  };

  const addCustomRight = () => {
    if (!customRightInput.trim() || !editingItem) return;
    const current = editingItem.rightsAvailable || [];
    if (!current.includes(customRightInput.trim())) {
      setEditingItem({ ...editingItem, rightsAvailable: [...current, customRightInput.trim()] });
    }
    setCustomRightInput('');
  };

  const filteredDistribution = distribution.filter((item) => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.synopsis.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.territories.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = typeFilter === 'ALL' || item.type === typeFilter;
    const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <ProtectedRoute requiredPermission="distribution.view">
      <AdminLayout>
        <div className="space-y-8 font-sans pb-16">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-zinc-900 pb-6">
            <div>
              <div className="inline-flex items-center gap-2 text-amber-500 font-mono text-xs tracking-widest uppercase mb-1">
                <Globe className="w-4 h-4" /> GLOBAL LICENSING & RIGHTS
              </div>
              <h1 className="text-3xl font-extrabold text-white uppercase font-mono">
                DISTRIBUTION CATALOGUE
              </h1>
              <p className="text-xs text-zinc-400 mt-1">
                Manage global film distribution slate, international territory licenses, and customize the homepage showcase section.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShowHeaderCustomizer(!showHeaderCustomizer)}
                className={`px-4 py-2.5 rounded-xl font-mono text-xs font-bold uppercase transition flex items-center gap-2 border ${
                  showHeaderCustomizer
                    ? 'bg-amber-500 text-black border-amber-500 shadow-lg shadow-amber-500/20'
                    : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700'
                }`}
              >
                <Sliders className="w-4 h-4 text-amber-400" />
                <span>{showHeaderCustomizer ? 'Close Section Customizer' : 'Customize Section Header'}</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  setEditingItem({
                    title: '',
                    type: 'Film',
                    synopsis: '',
                    territories: ['Worldwide'],
                    rightsAvailable: ['Theatrical', 'VOD / SVOD'],
                    posterUrl: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?auto=format&fit=crop&w=800&q=80',
                    status: 'PUBLISHED',
                    featured: true,
                    displayOrder: distribution.length + 1,
                  })
                }
                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase rounded-xl flex items-center gap-2 font-mono transition shadow-lg shadow-amber-500/10"
              >
                <Plus className="w-4 h-4" /> Add Distribution Title
              </button>
            </div>
          </div>

          {/* SECTION HEADER CUSTOMIZER PANEL (When Opened) */}
          {showHeaderCustomizer && (
            <form
              onSubmit={handleSaveSectionHeader}
              className="bg-zinc-900/90 border border-amber-500/40 rounded-3xl p-6 md:p-8 space-y-6 shadow-2xl relative overflow-hidden animate-fadeIn"
            >
              <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400">
                    <Sliders className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white uppercase font-mono">
                      Homepage Section 08 Customizer (Distribution & Rights)
                    </h2>
                    <p className="text-xs text-zinc-400">
                      Update the section header, eyebrow, description paragraph, and browse button shown on the homepage.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={savingHeader}
                    className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase rounded-xl flex items-center gap-2 font-mono transition"
                  >
                    {savingHeader ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    <span>{headerSaved ? 'Section Updated!' : 'Save Section Header'}</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1">Section Eyebrow Label</label>
                  <input
                    type="text"
                    value={sectionEyebrow}
                    onChange={(e) => setSectionEyebrow(e.target.value)}
                    placeholder="08 // GLOBAL LICENSING"
                    className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1">Section Main Title</label>
                  <input
                    type="text"
                    value={sectionHeading}
                    onChange={(e) => setSectionHeading(e.target.value)}
                    placeholder="DISTRIBUTION CATALOGUE"
                    className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono uppercase font-bold focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1">CTA Button Text</label>
                  <input
                    type="text"
                    value={sectionCtaLabel}
                    onChange={(e) => setSectionCtaLabel(e.target.value)}
                    placeholder="VIEW FULL CATALOGUE"
                    className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1">Section Subtitle / Description</label>
                  <input
                    type="text"
                    value={sectionDescription}
                    onChange={(e) => setSectionDescription(e.target.value)}
                    placeholder="International feature acquisitions, global theatrical rights, and OTT streaming licensing."
                    className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1">CTA Destination Route</label>
                  <input
                    type="text"
                    value={sectionCtaDestination}
                    onChange={(e) => setSectionCtaDestination(e.target.value)}
                    placeholder="/distribution"
                    className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-amber-400 text-xs font-mono focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>

              {/* Live Preview Box */}
              <div className="bg-zinc-950 p-4 rounded-2xl border border-zinc-800">
                <span className="text-[10px] font-mono text-zinc-500 uppercase block mb-2">Live Section Header Preview</span>
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-zinc-800 pb-3">
                  <div>
                    <span className="text-amber-500 text-xs font-mono font-bold tracking-widest uppercase block mb-1">
                      {sectionEyebrow}
                    </span>
                    <h3 className="text-xl font-bold font-serif-cinematic text-white uppercase">
                      {sectionHeading}
                    </h3>
                  </div>
                  <div className="text-xs font-mono text-amber-400 font-bold flex items-center gap-1">
                    {sectionCtaLabel} →
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Quick Metrics & Search Bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-mono text-zinc-400 uppercase block">Total Titles</span>
                <span className="text-2xl font-black text-white font-mono">{distribution.length}</span>
              </div>
              <Globe className="w-8 h-8 text-amber-400" />
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-mono text-zinc-400 uppercase block">Published Live</span>
                <span className="text-2xl font-black text-emerald-400 font-mono">
                  {distribution.filter((d) => d.status === 'PUBLISHED').length}
                </span>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-mono text-zinc-400 uppercase block">Feature Films</span>
                <span className="text-2xl font-black text-amber-400 font-mono">
                  {distribution.filter((d) => d.type === 'Film').length}
                </span>
              </div>
              <Film className="w-8 h-8 text-amber-400" />
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-mono text-zinc-400 uppercase block">Series / Docs</span>
                <span className="text-2xl font-black text-blue-400 font-mono">
                  {distribution.filter((d) => d.type !== 'Film').length}
                </span>
              </div>
              <Tv className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between bg-zinc-900/60 p-4 rounded-2xl border border-zinc-800">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search titles, synopsis, territories..."
                className="w-full pl-9 pr-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
              >
                <option value="ALL">All Formats</option>
                <option value="Film">Feature Film</option>
                <option value="Series">Series</option>
                <option value="Documentary">Documentary</option>
                <option value="Short">Short</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
              >
                <option value="ALL">All Statuses</option>
                <option value="PUBLISHED">Published</option>
                <option value="DRAFT">Draft</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          </div>

          {/* Title List / Cards */}
          {loading ? (
            <div className="text-center py-20 text-zinc-500 font-mono text-xs flex flex-col items-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-amber-400" />
              <span>Loading Distribution Titles...</span>
            </div>
          ) : filteredDistribution.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center space-y-4">
              <Globe className="w-12 h-12 text-zinc-600 mx-auto" />
              <h3 className="text-lg font-bold text-white font-mono">No Distribution Titles Found</h3>
              <p className="text-xs text-zinc-400 max-w-sm mx-auto">
                No titles match your current filters. Add a new distribution title or adjust your search.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredDistribution.map((item, index) => (
                <div
                  key={item.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 flex flex-col justify-between hover:border-amber-500/40 transition group"
                >
                  <div className="flex gap-4">
                    <div className="w-28 sm:w-32 aspect-[3/4] rounded-xl overflow-hidden bg-zinc-950 shrink-0 relative border border-zinc-800">
                      <StargazeImage
                        src={item.posterUrl}
                        alt={item.title}
                        focalPoint={item.focalPoint}
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-2 left-2 bg-black/80 px-2 py-0.5 rounded text-[9px] font-mono font-bold text-amber-400 uppercase">
                        {item.type}
                      </span>
                    </div>

                    <div className="space-y-2 flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="text-base font-bold text-white group-hover:text-amber-400 transition truncate">
                            {item.title}
                          </h3>
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-mono mt-1 ${
                              item.status === 'PUBLISHED'
                                ? 'bg-emerald-950 text-emerald-400 border border-emerald-800/50'
                                : 'bg-zinc-800 text-zinc-400'
                            }`}
                          >
                            {item.status}
                          </span>
                        </div>

                        {/* Order Buttons */}
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() => handleMove(index, 'up')}
                            className="p-1 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 text-zinc-400"
                            title="Move Up"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={index === distribution.length - 1}
                            onClick={() => handleMove(index, 'down')}
                            className="p-1 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 text-zinc-400"
                            title="Move Down"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <p className="text-xs text-zinc-400 line-clamp-2 leading-relaxed">
                        {item.synopsis}
                      </p>

                      <div className="space-y-1.5 pt-2 border-t border-zinc-800/80 text-[11px] font-mono">
                        <div className="text-zinc-400 truncate">
                          <span className="text-zinc-500">Territories:</span>{' '}
                          <strong className="text-amber-400">{item.territories.join(', ')}</strong>
                        </div>
                        <div className="text-zinc-400 truncate">
                          <span className="text-zinc-500">Rights:</span>{' '}
                          <strong className="text-zinc-200">{item.rightsAvailable.join(' • ')}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 mt-4 border-t border-zinc-800/60 text-xs font-mono">
                    <span className="text-zinc-500 text-[10px]">
                      Order #{item.displayOrder ?? index + 1}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingItem(item)}
                        className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-amber-400 font-bold flex items-center gap-1.5 transition"
                      >
                        <Edit3 className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(item)}
                        className="p-1.5 rounded-lg bg-zinc-800 hover:bg-red-950/60 text-zinc-400 hover:text-red-400 transition"
                        title="Delete Title"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* EDIT / CREATE TITLE MODAL */}
          {editingItem && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 sm:p-8 max-w-2xl w-full space-y-6 my-8 shadow-2xl relative">
                <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400">
                      <Globe className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white uppercase font-mono">
                        {editingItem.id ? 'Edit Distribution Title' : 'Add Distribution Title'}
                      </h2>
                      <p className="text-xs text-zinc-400">
                        Configure title specs, poster media, territory distribution, and available licensing rights.
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setEditingItem(null)}
                    className="p-2 text-zinc-400 hover:text-white rounded-lg bg-zinc-800 hover:bg-zinc-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSaveItem} className="space-y-5">
                  {/* Title & Type */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-mono text-zinc-400 mb-1">Film / Series Title *</label>
                      <input
                        type="text"
                        required
                        value={editingItem.title || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                        placeholder="e.g., COSMIC HORIZON"
                        className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1">Content Type</label>
                      <select
                        value={editingItem.type || 'Film'}
                        onChange={(e) => setEditingItem({ ...editingItem, type: e.target.value as any })}
                        className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                      >
                        <option value="Film">Feature Film</option>
                        <option value="Series">Series</option>
                        <option value="Documentary">Documentary</option>
                        <option value="Short">Short Film</option>
                      </select>
                    </div>
                  </div>

                  {/* Synopsis */}
                  <div>
                    <label className="block text-xs font-mono text-zinc-400 mb-1">Logline / Synopsis</label>
                    <textarea
                      rows={3}
                      value={editingItem.synopsis || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, synopsis: e.target.value })}
                      placeholder="Brief cinematic summary highlighting premise, accolades, or stars..."
                      className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs focus:outline-none focus:border-amber-500"
                    />
                  </div>

                  {/* Poster Image & Media Picker */}
                  <div className="space-y-2 bg-zinc-950 p-4 rounded-2xl border border-zinc-800">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-mono text-zinc-400">Poster Artwork Image URL</label>
                      <button
                        type="button"
                        onClick={() => setShowMediaPicker(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-mono font-bold text-amber-400 hover:text-amber-300"
                      >
                        <ImageIcon className="w-3.5 h-3.5" /> Media Library
                      </button>
                    </div>
                    <div className="flex gap-4 items-center">
                      <div className="w-16 h-20 rounded-lg overflow-hidden bg-zinc-900 shrink-0 border border-zinc-800">
                        {editingItem.posterUrl ? (
                          <img src={editingItem.posterUrl} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-600">
                            <ImageIcon className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <input
                        type="url"
                        value={editingItem.posterUrl || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, posterUrl: e.target.value })}
                        placeholder="https://images.unsplash.com/..."
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                      />
                    </div>
                  </div>

                  {/* Territories Multi-Select Tags */}
                  <div className="space-y-2">
                    <label className="block text-xs font-mono text-zinc-400">Available Territories</label>
                    <div className="flex flex-wrap gap-1.5">
                      {COMMON_TERRITORIES.map((territory) => {
                        const isSelected = (editingItem.territories || []).includes(territory);
                        return (
                          <button
                            key={territory}
                            type="button"
                            onClick={() => toggleTerritory(territory)}
                            className={`px-3 py-1 rounded-lg text-xs font-mono transition border ${
                              isSelected
                                ? 'bg-amber-500 text-black border-amber-500 font-bold'
                                : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-700'
                            }`}
                          >
                            {isSelected ? '✓ ' : '+ '} {territory}
                          </button>
                        );
                      })}
                    </div>
                    {/* Custom Territory Input */}
                    <div className="flex gap-2 pt-1">
                      <input
                        type="text"
                        value={customTerritoryInput}
                        onChange={(e) => setCustomTerritoryInput(e.target.value)}
                        placeholder="Add other custom territory..."
                        className="flex-1 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-lg text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addCustomTerritory();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addCustomTerritory}
                        className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-amber-400 text-xs font-mono font-bold rounded-lg"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Rights Available Multi-Select Tags */}
                  <div className="space-y-2">
                    <label className="block text-xs font-mono text-zinc-400">Licensing Rights Available</label>
                    <div className="flex flex-wrap gap-1.5">
                      {COMMON_RIGHTS.map((right) => {
                        const isSelected = (editingItem.rightsAvailable || []).includes(right);
                        return (
                          <button
                            key={right}
                            type="button"
                            onClick={() => toggleRight(right)}
                            className={`px-3 py-1 rounded-lg text-xs font-mono transition border ${
                              isSelected
                                ? 'bg-amber-500 text-black border-amber-500 font-bold'
                                : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-700'
                            }`}
                          >
                            {isSelected ? '✓ ' : '+ '} {right}
                          </button>
                        );
                      })}
                    </div>
                    {/* Custom Right Input */}
                    <div className="flex gap-2 pt-1">
                      <input
                        type="text"
                        value={customRightInput}
                        onChange={(e) => setCustomRightInput(e.target.value)}
                        placeholder="Add other rights category..."
                        className="flex-1 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-lg text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addCustomRight();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addCustomRight}
                        className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-amber-400 text-xs font-mono font-bold rounded-lg"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Status & Featured */}
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-zinc-800">
                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1">Publishing Status</label>
                      <select
                        value={editingItem.status || 'PUBLISHED'}
                        onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value as ContentStatus })}
                        className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                      >
                        <option value="PUBLISHED">PUBLISHED (Live)</option>
                        <option value="DRAFT">DRAFT (Hidden)</option>
                        <option value="ARCHIVED">ARCHIVED</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-3 pt-5">
                      <input
                        type="checkbox"
                        id="featDist"
                        checked={editingItem.featured ?? true}
                        onChange={(e) => setEditingItem({ ...editingItem, featured: e.target.checked })}
                        className="w-4 h-4 rounded text-amber-500 bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                      />
                      <label htmlFor="featDist" className="text-xs font-mono text-white select-none">
                        Featured on Homepage Slate
                      </label>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
                    <button
                      type="button"
                      onClick={() => setEditingItem(null)}
                      className="px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-mono text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold font-mono text-xs uppercase rounded-xl flex items-center gap-2 shadow-lg shadow-amber-500/10"
                    >
                      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      <span>{saving ? 'Saving...' : 'Save Distribution Title'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* DELETE CONFIRMATION MODAL */}
          {deleteConfirm && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-md w-full space-y-4 shadow-2xl">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-400">
                  <AlertCircle className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white font-mono">Delete Distribution Title?</h3>
                <p className="text-xs text-zinc-400">
                  Are you sure you want to remove <strong className="text-white font-bold">{deleteConfirm.title}</strong> from the distribution catalogue?
                </p>
                <div className="flex items-center justify-end gap-3 pt-3">
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-mono text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(deleteConfirm)}
                    className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white font-mono text-xs font-bold"
                  >
                    Yes, Delete Title
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Media Picker Modal */}
          {showMediaPicker && (
            <MediaPickerModal
              isOpen={true}
              onClose={() => setShowMediaPicker(false)}
              title="Select Poster Artwork"
              onSelect={(selection) => {
                if (editingItem) {
                  setEditingItem({
                    ...editingItem,
                    posterUrl: selection.url,
                    coverMediaId: selection.mediaId,
                    focalPoint: selection.focalPoint,
                  });
                }
                setShowMediaPicker(false);
              }}
            />
          )}
        </div>
      </AdminLayout>
    </ProtectedRoute>
  );
};
